/**
 * GlobalBusinessHub - Business features section with Install QR Code
 * Store badges are hidden on native apps (Huawei/GMS compatibility)
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ArrowRight,
  Globe,
  CreditCard,
  Zap
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';

// Check if running as native app
const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const GlobalBusinessHub = () => {
  const { t } = useTranslation();
  const appUrl = 'https://parachpay.com';
  const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.parachpay.app';
  const amazonAppstoreUrl = 'https://www.amazon.com/gp/product/B0H9JVSW7V';
  const appGalleryUrl = 'https://appgallery.huawei.com/app/C118318155';
  const showStoreBadges = !isNativeApp(); // Hide badges inside native app
  
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-4">
          {/* Text Content */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
              <span className="text-emerald-600 dark:text-emerald-400">{t('home.hub.title')}</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-md">
              {t('home.hub.desc')}
            </p>
          </div>
          
          {/* Globe with People Image */}
          <div className="flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_global-exchange-28/artifacts/jbs7xovf_Global%20Business%20Hub%20logo-1.png"
              alt="Global Business Hub - Connected World"
              className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-xl"
            />
          </div>
        </div>
        
        {/* Install ParachPay QR Code Section */}
        <div className="mt-10 flex flex-col items-center">
          <h3 className="text-xl font-semibold text-foreground mb-4">
            {t('home.hub.install')}
          </h3>
          <div className="bg-white p-3 rounded-lg shadow-lg border-2 border-slate-200">
            <QRCodeSVG
              value={appUrl}
              size={140}
              level="H"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#0f172a"
              imageSettings={{
                src: "/icons/icon-72x72.png",
                x: undefined,
                y: undefined,
                height: 28,
                width: 28,
                excavate: true,
              }}
            />
          </div>
          <p className="mt-3 text-sm text-muted-foreground text-center">
            {t('home.hub.scan')}
          </p>
          
          {/* App Store Badges - Hidden on native apps (Huawei/GMS compatibility) */}
          {showStoreBadges && (
            <>
              <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
                {/* Google Play Badge */}
                <a 
                  href={googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 rounded-lg"
                  data-testid="google-play-badge"
                >
                  <img 
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="h-12 w-auto"
                  />
                </a>
                
                {/* Amazon Appstore Badge */}
                <a 
                  href={amazonAppstoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 rounded-lg"
                  data-testid="amazon-appstore-badge"
                >
                  <img 
                    src="https://images-na.ssl-images-amazon.com/images/G/01/mobile-apps/devportal2/res/images/amazon-appstore-badge-english-black.png"
                    alt="Available at Amazon Appstore"
                    className="h-9 w-auto"
                  />
                </a>
                
                {/* Huawei AppGallery Badge (official Huawei artwork) */}
                <a 
                  href={appGalleryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 rounded-lg"
                  data-testid="huawei-appgallery-badge"
                >
                  <img 
                    src="/badges/huawei-appgallery-badge.png"
                    alt={t('home.badges.huaweiAlt')}
                    className="h-9 w-auto"
                  />
                </a>
              </div>
              
              {/* Store availability text */}
              <p className="mt-3 text-xs text-muted-foreground">
                {t('home.hub.availability')}
              </p>
            </>
          )}
        </div>
        
        {/* Directory Links */}
        <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3 justify-center md:justify-start">
          {/* Travel Agencies & Airlines Link */}
          <Link 
            to="/register"
            className="inline-flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Globe className="w-4 h-4" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">{t('home.hub.airlines')}</p>
              <p className="text-xs text-emerald-100">{t('notices.airlinesDirectory')}</p>
            </div>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          
          {/* Hotels & Accommodation Link */}
          <Link 
            to="/register"
            className="inline-flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            <div className="p-1.5 bg-white/20 rounded-lg">
              <CreditCard className="w-4 h-4" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">{t('home.hub.hotels')}</p>
              <p className="text-xs text-blue-100">{t('notices.hotelsDirectory')}</p>
            </div>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          
          {/* Banks & Banking Link */}
          <Link 
            to="/register"
            className="inline-flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Zap className="w-4 h-4" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">{t('home.hub.banks')}</p>
              <p className="text-xs text-amber-100">{t('notices.banksDirectory')}</p>
            </div>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        
        {/* Get Started Call to Action */}
        <div className="mt-4 text-center md:text-left">
          <Link 
            to="/register"
            className="inline-flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
          >
            <span>{t('home.hub.getStarted')}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default GlobalBusinessHub;
